import _ from "lodash";
import { useState, useEffect } from "react";
import {
  FormProps,
  Modal,
  Form,
  Dropdown,
  TextArea,
  Button,
  Table,
  DropdownItemProps,
} from "semantic-ui-react";
import { useCurrentUser } from "../hooks/auth-hook";
import { ModalControlData, useModalControl } from "../hooks/commonHooks";
import { trimSetField, useFormData } from "../hooks/formHooks";
import { restRequest } from "../http";
import { FileContainerType } from "../models/common_model";
import {
  AnalyzedSummary,
  AnalyzedUnit,
  AnalyzedUnitConfig,
  IssueOverview,
} from "../models/analyzed_unit_model";
import { fieldError, formatDateTime } from "../utils";
import { ImagesInput } from "./commons/ImagesInput";
import { RootCauseComponent } from "./commons/RootCauseComponent";
import { TextEditor } from "./commons/TextEditor";

export function AnalyzedUnitEditor(props: {
  id?: number;
  fileInfo?: { product: number; serial: string; order: string };
  modalControl: ModalControlData<boolean>;
  onFinish: (data: AnalyzedUnit) => void;
  onExit?:() => void;
}) {
  const currentUser = useCurrentUser();
  const { modalControl, fileInfo } = props;
  const [id, setId] = useState<number>();
  const [config, setConfig] = useState<AnalyzedUnitConfig>();
  const [productionOptions, setProductionOptions] = useState<
    DropdownItemProps[]
  >([]);
  const [productionAdditionOptions, setProductionAdditionOptions] = useState<
    DropdownItemProps[]
  >([]);
  const [summaries, setSummaries] = useState<AnalyzedSummary[]>([]);
  const [finalSerialNumber, setFinalSerialNumber] = useState(
    (fileInfo && fileInfo.serial) || ""
  );
  const [reloadSerial, setReloadSerial] = useState(0);
  const summaryEditModalControl = useModalControl();
  const rootCauseModalControl = useModalControl();
  const [rootCausesOptions, setRootCauseOptions] = useState<
    DropdownItemProps[]
  >([]);
  const [refreshRootCause, setRefreshRootCause] = useState(0);
  const [editingSummary, setEditingSummary] = useState<AnalyzedSummary>(
    {} as any
  );

  const [issueOverviewList, setIssueOverviewList] = useState<IssueOverview[]>([]);
  const [searchOverviewKey, setSearchOverviewKey] = useState('');

  const readOnly = modalControl.data;

  useEffect(() => {
    restRequest<DropdownItemProps[]>(
      `commons/root_causes/search_for_selection`
    ).then((options) => {
      setRootCauseOptions(options);
    });
  }, [refreshRootCause]);

  useEffect(() => {
    if (!modalControl.props.open) {
      return;
    }

    restRequest<AnalyzedUnitConfig>("analyzed_unit/config").then(setConfig);
  }, [modalControl.props.open]);

  const [data, setField, setData] = useFormData<AnalyzedUnit>({
    ...fileInfo,
    case_status: 0,
    owner: currentUser.name,
  } as any);
  const [invalidError, setInvalidError] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [loadingFiles, setLoadingFiles] = useState(false);

  const isCaseClosed = data && data.case_status === 1;

  useEffect(() => {
    setId(props.id);
  }, [props.id]);

  useEffect(() => {
    setFinalSerialNumber((fileInfo && fileInfo.serial) || "");
  }, [fileInfo && fileInfo.serial]);

  useEffect(() => {
    setProductionOptions([
      ...(config?.products || [])
        .sort()
        .map((p) => ({ text: p, value: p, key: p })),
      ...productionAdditionOptions,
    ]);
  }, [config?.products, productionAdditionOptions]);

  useEffect(() => {
    if (!modalControl.props.open) {
      return;
    }

    setInvalidError({});

    if (props.id) {
      setLoading(true);
      restRequest<AnalyzedUnit>("analyzed_unit/" + props.id)
        .then((d) => {
          setData(d);
          setFinalSerialNumber(d.serial);
        })
        .finally(() => setLoading(false));
    } else if (fileInfo && fileInfo.product) {
      setLoading(true);
      restRequest<AnalyzedUnit>(
        "analyzed_unit/by_product_and_serial",
        fileInfo,
        "post"
      )
        .then((d) => {
          if (d) {
            setId(d.id);
            setData((od) => ({ ...d, owner: od.owner || currentUser.name }));
            setFinalSerialNumber(d.serial);
          } else {
            setData(
              (od) =>
                ({
                  ...fileInfo,
                  case_status: 0,
                  owner: od.owner || currentUser.name,
                  order: fileInfo.order || od.order,
                } as any)
            );
            setId(undefined);
            setFinalSerialNumber(fileInfo.serial);
          }
        })
        .finally(() => setLoading(false));
    } else {
      setData(
        (od) =>
          ({
            case_status: 0,
            owner: currentUser.name,
            order: '',
          } as any)
      );
      setId(undefined);
      setFinalSerialNumber("");
    }
  }, [
    modalControl.props.open,
    props.id,
    fileInfo && fileInfo.product,
    fileInfo && fileInfo.serial,
    fileInfo && fileInfo.order,
  ]);

  useEffect(() => {
    if (finalSerialNumber && data.product) {
      setLoadingFiles(true);

      const abortController = new AbortController();
      restRequest<AnalyzedSummary[]>(
        "analyzed_unit/find_summaries",
        {
          product: data.product,
          serial_number: finalSerialNumber,
          analyzed_unit_id: id,
        },
        "post",
        abortController
      )
        .then((summaries) => {
          if (!summaries) {
            return;
          }
          let maxOrder = "";
          if (summaries.length > 0) {
            const orders = summaries.map((s) => s.order);
            const orderCounts = _.countBy(orders);
            maxOrder = orders.sort(
              (o1, o2) => orderCounts[o1] - orderCounts[o2]
            )[0];
          }
          setSummaries(summaries);
          setData((d) => ({ ...d, order: d.order || maxOrder }));
        })
        .finally(() => setLoadingFiles(false));

        return () => abortController.abort();
    } else {
      setSummaries([]);
    }
  }, [data.product, finalSerialNumber, id, reloadSerial]);


  useEffect(() => {
    restRequest<IssueOverview[]>(
      `analyzed_unit/search_issue_overviews`, {product: data.product, name: searchOverviewKey, overview_id: data.issue_overview_id}, 'post'
    ).then(setIssueOverviewList)
  }, [searchOverviewKey, data.product])


  const isCreate = !id;
  const onSubmit = (
    event: React.FormEvent<HTMLFormElement>,
    formProps: FormProps
  ) => {
    event.preventDefault();

    setLoading(true);

    let url: string;
    let formData = {
      ...data,
      summaries: summaries.map((s) =>
        _.pick(s, "file_id", "update_status", "remark")
      ),
    };
    if (isCreate) {
      url = "analyzed_unit/";
    } else {
      url = `analyzed_unit/${id}`;
    }

    restRequest<AnalyzedUnit>(url, formData, isCreate ? "post" : "put")
      .then(
        (result) => {
          props.onFinish(result);
          setData(result);
          setId(result.id);
          setInvalidError({});
          props.onExit && props.onExit();
        },
        (resp) => {
          if (resp.status === 2) {
            setInvalidError(resp.data);
          }
        }
      )
      .finally(() => setLoading(false));
  };

  let actionName: string, buttonName: string;
  if (isCreate) {
    actionName = "New";
    buttonName = "Create";
  } else {
    actionName = "Edit";
    buttonName = "Save";
  }

  return (
    <>
      <Modal {...modalControl.props} onClose={() => {
        modalControl.setOpen(false);
        props.onExit && props.onExit();
      }} size="fullscreen">
        <Modal.Header>{actionName} Analyzed Unit</Modal.Header>
        <Modal.Content>
          <Form onSubmit={onSubmit} loading={loading}>
            <Form.Group widths="equal">
              <Form.Input
                required
                label="Product"
                error={fieldError("product", invalidError)}
              >
                {isCreate ? (
                  <Dropdown
                    name="product"
                    value={data.product || ""}
                    onChange={setField}
                    options={productionOptions}
                    allowAdditions
                    onAddItem={(e, d) => {
                      setProductionAdditionOptions((os) => [
                        ...os,
                        {
                          key: d.value,
                          text: d.value as string,
                          value: d.value as string,
                          isAdded: true,
                        },
                      ]);
                    }}
                    search
                    selection
                    fluid
                  ></Dropdown>
                ) : (
                  <span>{data.product}</span>
                )}
              </Form.Input>
              {isCreate ? (
                <Form.Input
                  onBlur={() => {
                    setFinalSerialNumber(data.serial?.trim());
                    setData(d => ({...d, serial: d.serial?.trim()}));
                  }}
                  onKeyDown={(e: any) => {
                    if (e.keyCode === 13) {
                      e.stopPropagation();
                      e.preventDefault();

                      setReloadSerial(s => s + 1);
                      setFinalSerialNumber(data.serial);
                    }
                  }}
                  required
                  label="Serial"
                  name="serial"
                  value={data["serial"] || ""}
                  onChange={setField}
                  error={fieldError("serial", invalidError)}
                  loading={loadingFiles}
                ></Form.Input>
              ) : (
                <Form.Input required label="Serial">
                  <span>{data.serial}</span>
                </Form.Input>
              )}
              <Form.Input
                required
                label="Order"
                name="order"
                value={data["order"] || ""}
                onChange={setField} onBlur={trimSetField(setField)}
                error={fieldError("order", invalidError)}
              ></Form.Input>
              <Form.Input
                required
                label="Owner"
                name="owner"
                value={data["owner"] || ""}
                onChange={setField} onBlur={trimSetField(setField)}
                error={fieldError("owner", invalidError)}
              ></Form.Input>
            </Form.Group>
            <Form.Group widths="equal">
              <Table>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Build</Table.HeaderCell>
                    <Table.HeaderCell>Station</Table.HeaderCell>
                    <Table.HeaderCell>File</Table.HeaderCell>
                    <Table.HeaderCell>Date</Table.HeaderCell>
                    <Table.HeaderCell>Sequence Status</Table.HeaderCell>
                    <Table.HeaderCell>Fail Measurements</Table.HeaderCell>
                    <Table.HeaderCell>Update Status</Table.HeaderCell>
                    <Table.HeaderCell></Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {summaries.map((s, i) => {
                    return (
                      <Table.Row key={s.file_id}>
                        <Table.Cell>{s.order}</Table.Cell>
                        <Table.Cell>{s.testing_station}</Table.Cell>
                        <Table.Cell title={s.file_path}>
                          {_.last(s.file_path.split("/"))}
                        </Table.Cell>
                        <Table.Cell>{formatDateTime(s.date, true)}</Table.Cell>
                        <Table.Cell>
                          {
                            ["Unknown", "Pass", "Fail", "Invalid", "Stopped"][
                              s.status
                            ]
                          }
                        </Table.Cell>
                        <Table.Cell>{s.fail_measurements}</Table.Cell>
                        <Table.Cell>
                          {config?.AVAILABLE_UPDATE_STATUSES &&
                            config.AVAILABLE_UPDATE_STATUSES[
                              s.update_status === s.status ? 0 : s.update_status
                            ]}
                        </Table.Cell>
                        <Table.Cell>
                          <Button
                            size="mini"
                            target="_blank"
                            as="a"
                            href={`/testing_file_visual/${s.file_id}`}
                            icon="search"
                          ></Button>
                          {!readOnly && (
                            <Button
                              size="mini"
                              target="_blank"
                              as="a"
                              icon="edit"
                              onClick={(e) => {
                                e.preventDefault();

                                setEditingSummary({ ...s });
                                summaryEditModalControl.setOpen(true);
                              }}
                            ></Button>
                          )}
                        </Table.Cell>
                      </Table.Row>
                    );
                  })}
                </Table.Body>
              </Table>
            </Form.Group>
            <Form.Group widths="equal">
              <Form.Input
                label="Issue Name"
                name="issue_overview_id"
                error={fieldError("issue_overview_id", invalidError)}
              >
                <Dropdown
                  name="issue_overview_id"
                  value={data.issue_overview_id || 0}
                  onChange={(e, d) => {
                    setField(e, d);
                    if (isCreate) {
                      const issue = issueOverviewList.find(i => i.id == d.value);
                      if (issue) {
                        setData(d => ({ ...d, failure_description: issue.description, failure_analysis: issue.analysis, action: issue.actions }))
                      }
                    }
                  }}
                  options={[{ text: "None", value: 0, key: 0 }].concat(issueOverviewList.map((p, i) => ({ text: p.name, value: p.id, key: p.id })))}
                  onSearchChange={(e, d) => { 
                    setSearchOverviewKey(d.value as string);                    
                  }}
             
                  search
                  selection
                  fluid
                ></Dropdown>
              </Form.Input>
            </Form.Group>
            <Form.Group widths="equal">
              <Form.Input
                label="Case Status"
                error={fieldError("case_status", invalidError)}
              >
                <Dropdown
                  name="case_status"
                  value={data.case_status || 0}
                  onChange={setField}
                  options={(config?.AVAILABLE_CASE_STATUSES || []).map(
                    (p, i) => ({ text: p, value: i, key: p })
                  )}
                  search
                  selection
                  fluid
                ></Dropdown>
              </Form.Input>
              <Form.Input
                required={isCaseClosed}
                label={
                  <>
                    <label
                      style={{ display: "inline-block", marginRight: "1rem" }}
                    >
                      Root Cause
                    </label>{" "}
                    <Button
                      onClick={(e) => rootCauseModalControl.setOpen(true)}
                      compact
                      color="blue"
                      icon="edit outline"
                      size="mini"
                      as="a"
                    ></Button>
                  </>
                }
                error={fieldError("root_cause", invalidError)}
              >
                <Dropdown
                  name="root_cause"
                  value={data.root_cause || ""}
                  onChange={setField}
                  options={rootCausesOptions}
                  search
                  selection
                  fluid
                ></Dropdown>
              </Form.Input>
              <Form.Input
                required={isCaseClosed}
                label="Resolved By"
                error={fieldError("resolved_by", invalidError)}
              >
                <Dropdown
                  name="resolved_by"
                  value={data.resolved_by || 0}
                  onChange={setField}
                  options={(config?.AVAILABLE_RESOLVED_BY || [])
                    .sort()
                    .map((p, i) => ({ text: p, value: p, key: p }))}
                  search
                  selection
                  fluid
                ></Dropdown>
              </Form.Input>
            </Form.Group>
            <Form.Group widths="equal">
              <Form.Input
                label="Failure Description"
                required={isCaseClosed}
                error={fieldError("failure_description", invalidError, "below")}
              >
                <TextEditor
                  name="failure_description"
                  value={data.failure_description || ""}
                  onChange={setField}
                />
              </Form.Input>
            </Form.Group>
            <Form.Group widths="equal">
              <Form.Input
                label="Failure Analysis"
                required={isCaseClosed}
                error={fieldError("failure_analysis", invalidError, "below")}
              >
                <TextEditor
                  name="failure_analysis"
                  value={data.failure_analysis || ""}
                  onChange={setField}
                />
              </Form.Input>
            </Form.Group>
            <Form.Group widths="equal">
              <Form.Input
                label="Action"
                required={isCaseClosed}
                error={fieldError("action", invalidError, "below")}
              >
                <TextEditor
                  name="action"
                  value={data.action || ""}
                  onChange={setField}
                />
              </Form.Input>
            </Form.Group>
            <Form.Group widths="equal">
              <Form.Input
                label="Remark"
                error={fieldError("remark", invalidError, "below")}
              >
                <TextEditor
                  name="remark"
                  value={data.remark || ""}
                  onChange={setField}
                />
              </Form.Input>
            </Form.Group>
            <Form.Group widths="equal">
              <Form.Input label="Pictures">
                <ImagesInput
                  name="pictures"
                  value={data.pictures || []}
                  onChange={setField}
                  containerType={FileContainerType.WaiveUnitPicture}
                  containerId={id || 0}
                ></ImagesInput>
              </Form.Input>
            </Form.Group>

            <div>
              {!readOnly && <Button primary>{buttonName}</Button>}
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              <Button as="a" onClick={(e) => {
                modalControl.setOpen(false);
                props.onExit && props.onExit();
              }}>
                Cancel
              </Button>
            </div>
          </Form>
        </Modal.Content>
      </Modal>

      <Modal {...summaryEditModalControl.props} size="small">
        <Modal.Header>
          Summary for{" "}
          {editingSummary.file_path &&
            _.last(editingSummary.file_path.split("/"))}
        </Modal.Header>
        <Modal.Content>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              setSummaries((ss) =>
                ss.map((s) => {
                  if (s.file_id === editingSummary.file_id) {
                    return {
                      ...s,
                      update_status: editingSummary.update_status,
                      remark: editingSummary.remark,
                    };
                  }
                  return s;
                })
              );
              setEditingSummary({} as any);

              summaryEditModalControl.setOpen(false);
            }}
            loading={loading}
          >
            <Form.Group widths="equal">
              <Form.Input label="Update Status">
                <Dropdown
                  name="update_status"
                  value={editingSummary.update_status || 0}
                  onChange={(e, d) => {
                    setEditingSummary((s) => ({
                      ...s,
                      update_status: d.value as number,
                    }));
                  }}
                  options={(config?.AVAILABLE_UPDATE_STATUSES || []).map(
                    (p, i) => ({ text: p, value: i, key: i })
                  )}
                  search
                  selection
                  fluid
                ></Dropdown>
              </Form.Input>
            </Form.Group>
            <Form.Group widths="equal">
              <Form.Input label="Remark">
                <TextArea
                  name="comment"
                  value={editingSummary.remark || ""}
                  onChange={(e, d) => {
                    setEditingSummary((s) => ({
                      ...s,
                      remark: d.value as string,
                    }));
                  }}
                />
              </Form.Input>
            </Form.Group>

            <div>
              <Button primary>Save</Button>
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              <Button
                as="a"
                onClick={(e) => summaryEditModalControl.setOpen(false)}
              >
                Close
              </Button>
            </div>
          </Form>
        </Modal.Content>
      </Modal>

      <Modal
        open={rootCauseModalControl.props.open}
        onClose={() => {
          rootCauseModalControl.props.onClose();
          setRefreshRootCause((n) => n + 1);
        }}
        size="large"
        closeIcon="close"
      >
        <Modal.Header> Root Cause </Modal.Header>
        <Modal.Content>
          <RootCauseComponent />
        </Modal.Content>
        <Modal.Actions>
          <Button
            as="a"
            className="cancel"
            onClick={(e) => {
              e.preventDefault();
              rootCauseModalControl.props.onClose();
              setRefreshRootCause((n) => n + 1);
            }}
          >
            Close
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
}
